// ============================================================================
// cg-selectors.ts — dérive, depuis un bloc `constrained_geometry`, les options
// sélectionnables (points / segments / angles) avec des libellés lisibles
// ([AB], ∠ABC). PUR & testable. Permet aux formulaires de proposer des choix
// nommés plutôt que des IDs bruts (cf. docs/visual-constraint-engine.md §9).
// ============================================================================

import type { ConstrainedGeometryBlock } from './visual-blocks';

export interface CgSelectorOption {
  value: string;
  label: string;
}

export interface CgSegmentOption {
  a: string;
  b: string;
  label: string;
}

export interface CgAngleOption {
  vertex: string;
  a: string;
  b: string;
  label: string;
}

export interface CgSelectors {
  points: CgSelectorOption[];
  segments: CgSegmentOption[];
  angles: CgAngleOption[];
}

/** Clé non ordonnée d'une paire (pour dédupliquer AB == BA). */
const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export function deriveSelectors(block: ConstrainedGeometryBlock): CgSelectors {
  const nameOf = new Map<string, string>();
  for (const p of block.points) nameOf.set(p.id, p.name || p.id);
  const label = (id: string) => nameOf.get(id) ?? id;

  const points: CgSelectorOption[] = block.points.map((p) => ({
    value: p.id,
    label: p.name || p.id,
  }));

  // Adjacence des points reliés par un tracé segment/line (graphe non orienté).
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (a === b || !nameOf.has(a) || !nameOf.has(b)) return;
    (adjacency.get(a) ?? adjacency.set(a, new Set()).get(a)!).add(b);
    (adjacency.get(b) ?? adjacency.set(b, new Set()).get(b)!).add(a);
  };

  const segSeen = new Set<string>();
  const segments: CgSegmentOption[] = [];
  for (const d of block.drawables) {
    if (d.kind !== 'segment' && d.kind !== 'line') continue;
    link(d.a, d.b);
    const key = pairKey(d.a, d.b);
    if (segSeen.has(key) || !nameOf.has(d.a) || !nameOf.has(d.b)) continue;
    segSeen.add(key);
    segments.push({ a: d.a, b: d.b, label: `[${label(d.a)}${label(d.b)}]` });
  }

  // Angles : pour chaque sommet, toute paire de voisins distincts → ∠(a, vertex, b).
  const angleSeen = new Set<string>();
  const angles: CgAngleOption[] = [];
  for (const [vertex, nbrSet] of adjacency) {
    const nbrs = [...nbrSet];
    for (let i = 0; i < nbrs.length; i++) {
      for (let j = i + 1; j < nbrs.length; j++) {
        const a = nbrs[i]!;
        const b = nbrs[j]!;
        const key = `${vertex}:${pairKey(a, b)}`;
        if (angleSeen.has(key)) continue;
        angleSeen.add(key);
        angles.push({ vertex, a, b, label: `∠${label(a)}${label(vertex)}${label(b)}` });
      }
    }
  }

  return { points, segments, angles };
}
